import { computed, ref, useStore } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { useI18n } from "@/composables";
import { getLtksStore } from "@/store/ltks";
import mapData from "@/utils/transform/map-data";

import useConsumerSearchApi from "./-api";
import { isModelStale } from "../-utils";
import { useBrandsStore } from "../brands";
import { getProfilesStore } from "../profiles";

interface RetailerProductLinksStoreContext {
  $api: ReturnType<typeof useConsumerSearchApi>;
}

export const useConsumerSearchStore = defineStore(
  "consumer-search",
  () => {
    const store = useStore();
    const ltksStore = getLtksStore(store);
    const profilesStore = getProfilesStore(store);
    const brandsStore = useBrandsStore();
    const i18n = useI18n();

    // State
    const ltkIdMap = ref<ConsumerSearchPostIDResultMap>({});
    const productIdMap = ref<ConsumerSearchProductIDResultMap>({});
    const profileIdMap = ref<ConsumerSearchProfileIDResultMap>({});
    const ltksFeedMap = ref<ConsumerSearchLtksFeedMap>({});
    const profilesFeedMap = ref<ConsumerSearchProfilesFeedMap>({});
    const brandsFeedMap = ref<ConsumerSearchBrandsFeedMap>({});
    const queryType = ref<SearchEventQueryType>();

    const locale = computed(() => i18n.localeProperties.iso);
    const countryCode = computed(() => {
      const localeValue = locale.value;
      if (!localeValue) {
        return;
      }
      return new Intl.Locale(localeValue).region;
    });

    // Actions
    async function fetchLtkSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      // Avoid refetching if we have fresh data
      if (!isModelStale(ltksFeedMap.value[data.query])) {
        return;
      }
      resetLtksFeed(data.query);
      const response = await this.$api.fetchLtkIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newLtks = response.data.items;
      await ltksStore.fetchLtksByIds(newLtks.map((item) => item.id));
      ltksFeedMap.value = Object.assign({}, ltksFeedMap.value, {
        [data.query]: {
          ...response.data,
          fetchedAt: Date.now(),
        },
      });
      ltkIdMap.value = Object.assign({}, ltkIdMap.value, mapData("id", newLtks));
      productIdMap.value = Object.assign(
        {},
        productIdMap.value,
        mapData(
          "id",
          newLtks.flatMap((item) => item.children || []),
        ),
      );
    }

    async function fetchMoreLtkSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchLtkIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newLtks = response.data.items;
      await ltksStore.fetchLtksByIds(newLtks.map((item) => item.id));
      const existingFeed = ltksFeedMap.value[data.query] || {};
      ltksFeedMap.value = Object.assign({}, ltksFeedMap.value, {
        [data.query]: {
          ...existingFeed,
          ...response.data,
          items: [...(existingFeed.items || []), ...newLtks],
        },
      });
      ltkIdMap.value = Object.assign({}, ltkIdMap.value, mapData("id", newLtks));
      productIdMap.value = Object.assign(
        {},
        productIdMap.value,
        mapData(
          "id",
          newLtks.flatMap((item) => item.children || []),
        ),
      );
    }

    // Actions
    async function fetchProfileSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      // Avoid refetching if we have fresh data
      if (!isModelStale(profilesFeedMap.value[data.query])) {
        return;
      }
      resetProfilesFeed(data.query);
      const response = await this.$api.fetchProfileIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newProfiles = response.data.items;
      await profilesStore.fetchProfilesByIds({ ids: newProfiles.map((item) => item.id) });
      profilesFeedMap.value = Object.assign({}, profilesFeedMap.value, {
        [data.query]: {
          ...response.data,
          fetchedAt: Date.now(),
        },
      });
      profileIdMap.value = Object.assign({}, profileIdMap.value, mapData("id", newProfiles));
    }

    async function fetchMoreProfileSearchResults(
      this: RetailerProductLinksStoreContext,
      data: Omit<PostConsumerSearchRequest, "types">,
    ) {
      const response = await this.$api.fetchProfileIDSearchResults({
        ...data,
        locale: locale.value,
        countryCode: countryCode.value,
      });
      const newProfiles = response.data.items;
      await profilesStore.fetchProfilesByIds({ ids: newProfiles.map((item) => item.id) });
      const existingFeed = profilesFeedMap.value[data.query] || {};
      profilesFeedMap.value = Object.assign({}, profilesFeedMap.value, {
        [data.query]: {
          ...existingFeed,
          ...response.data,
          items: [...(existingFeed.items || []), ...newProfiles],
        },
      });
      profileIdMap.value = Object.assign({}, profileIdMap.value, mapData("id", newProfiles));
    }

    async function fetchBrandSearchResults(
      this: RetailerProductLinksStoreContext,
      data: PostConsumerSearchBrandRequest,
    ) {
      // Avoid refetching if we have fresh data
      if (!isModelStale(brandsFeedMap.value[data.query])) {
        return;
      }
      resetBrandsFeed(data.query);
      const response = await this.$api.fetchBrandSearchResults(data);
      const newBrands = response.data.brands;
      brandsStore.profiles = Object.assign({}, brandsStore.profiles, mapData("id", newBrands));
      brandsFeedMap.value = Object.assign({}, brandsFeedMap.value, {
        [data.query]: {
          ...response.data,
          fetchedAt: Date.now(),
        },
      });
    }

    function resetLtksFeed(feed: string) {
      ltksFeedMap.value = Object.assign({}, ltksFeedMap.value, {
        [feed]: undefined,
      });
    }

    function resetProfilesFeed(feed: string) {
      profilesFeedMap.value = Object.assign({}, profilesFeedMap.value, {
        [feed]: undefined,
      });
    }

    function resetBrandsFeed(feed: string) {
      brandsFeedMap.value = Object.assign({}, brandsFeedMap.value, {
        [feed]: undefined,
      });
    }

    return {
      ltkIdMap,
      productIdMap,
      profileIdMap,
      ltksFeedMap,
      profilesFeedMap,
      brandsFeedMap,
      queryType,
      fetchLtkSearchResults,
      fetchMoreLtkSearchResults,
      fetchProfileSearchResults,
      fetchMoreProfileSearchResults,
      fetchBrandSearchResults,
    };
  },
  {
    api: useConsumerSearchApi,
  },
);
